import { Button } from '../../../../components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import StaffActionMenu from '../components/StaffActionMenu';
import useFetchOrganization from '../api/useFetchOrganization';
import type { User } from '../types/Organization';
import UserTypeTag from '../components/UserTypeTag';
import { useState, useCallback, useEffect } from 'react';
import NewStaffPage from './NewStaffPage';
import StaffForm from './StaffForm';
import OrganizationSettingsTabs from '../components/SettingsTabs';
import useFetchIam from '../../api/useFetchIam';
import useUpdateOrganizationStaff from '../api/useUpdateOrganizationStaff';
import useUpdateOrganizationStaffResetPassword from '../api/useUpdateOrganizationStaffResetPassword';
import useUpdateOrganizationStaffAttachAvatar from '../api/useUpdateOrganizationStaffAttachAvatar';
import useReorderOrganizationMembers from '../api/useReorderOrganizationMembers';
import { showSuccessToast, showErrorToast } from '../../job_change_processes/lib/toast';
import { useNavigate } from '@tanstack/react-router';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { MdDragIndicator } from 'react-icons/md';

function SortableMemberRow({
  member,
  index,
  totalLength,
  isAdmin,
  onEditStaff,
  refetchOrganization,
}: {
  member: User;
  index: number;
  totalLength: number;
  isAdmin: boolean;
  onEditStaff: (staffId: number) => void;
  refetchOrganization: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: member.id,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'bg-gray-50' : ''}>
      <td
        className={`overflow-hidden border-r border-solid border-gray-200 px-6 py-[10px] align-middle ${index !== totalLength - 1 ? 'border-b' : ''}`}
      >
        <div className="flex min-w-0 items-center gap-2">
          {isAdmin && (
            <div
              {...attributes}
              {...listeners}
              className="shrink-0 cursor-move text-gray-400 hover:text-gray-600"
            >
              <MdDragIndicator className="size-5" />
            </div>
          )}
          <div className="min-w-0 flex-1 truncate text-base text-gray-900">{member.name}</div>
        </div>
      </td>
      <td
        className={`overflow-hidden border-r border-solid border-gray-200 px-6 py-[10px] align-middle ${index !== totalLength - 1 ? 'border-b' : ''}`}
      >
        <div className="truncate text-base text-gray-900">{member.email}</div>
      </td>
      <td
        className={`w-[160px] border-r border-solid border-gray-200 px-6 py-[10px] align-middle whitespace-nowrap ${index !== totalLength - 1 ? 'border-b' : ''}`}
      >
        <UserTypeTag userType={member.is_admin ? 'admin' : 'user'} status={member.status} />
      </td>
      {isAdmin && (
        <td
          className={`w-x3l border-solid border-gray-200 align-middle ${index !== totalLength - 1 ? 'border-b' : ''}`}
        >
          <div className="flex items-center justify-center">
            <StaffActionMenu
              staffId={member.id}
              refetchOrganization={refetchOrganization}
              onEditStaff={onEditStaff}
            />
          </div>
        </td>
      )}
    </tr>
  );
}

export default function MemberSettings() {
  const { data: organization, refetch: refetchOrganization } = useFetchOrganization();
  const [isNewStaffPageOpen, setIsNewStaffPageOpen] = useState(false);
  const [isStaffFormOpen, setIsStaffFormOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<User | null>(null);
  const { data: iam } = useFetchIam();
  const { mutateAsync: updateInfo } = useUpdateOrganizationStaff();
  const { mutateAsync: updatePassword } = useUpdateOrganizationStaffResetPassword();
  const { mutateAsync: updateAvatar } = useUpdateOrganizationStaffAttachAvatar();
  const { mutateAsync: reorderMembers } = useReorderOrganizationMembers();
  const [resetPasswordFields, setResetPasswordFields] = useState(false);
  const navigate = useNavigate({ from: '/agent/settings' });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // アクティブメンバーを取得（サポートアカウントを除外）
  const activeMembers = organization?.users.filter(
    (user: User) => user.status === 'active' && !user.is_support
  );

  // 表示用のメンバーリスト（order_number順にソート済み）
  // 楽観的更新のためにローカル状態で管理
  const [displayMembers, setDisplayMembers] = useState<User[]>([]);

  // organizationが変更されたら、displayMembersを更新
  useEffect(() => {
    if (organization?.users) {
      setDisplayMembers(organization.users);
    }
  }, [organization?.users]);

  const handleNewStaffPageClose = () => {
    setIsNewStaffPageOpen(false);
    refetchOrganization();
  };

  const handleEditStaff = (staffId: number) => {
    // 自分自身を編集しようとした場合は、アカウント設定タブに遷移
    if (iam?.current_user?.id === staffId) {
      navigate({
        to: '/agent/settings',
        search: (prev) => ({ ...prev, t: 'account' }),
        replace: true,
      });
      return;
    }

    const staff = organization?.users.find((user) => user.id === staffId);
    if (staff) {
      setSelectedStaff(staff);
      setIsStaffFormOpen(true);
    }
  };

  const handleUpdateInfo = async (
    name: string,
    email: string,
    is_admin: boolean,
    avatar?: File | null
  ) => {
    if (selectedStaff?.id) {
      try {
        if (avatar) {
          await updateAvatar({ id: selectedStaff.id, avatar });
          await updateInfo({ id: selectedStaff.id, name, email, is_admin });
        } else {
          await updateInfo({ id: selectedStaff.id, name, email, is_admin });
        }
        showSuccessToast('メンバーの登録情報を保存しました。');
      } catch (error) {
        showErrorToast('メンバーの登録情報を保存できませんでした。');
      }
    }
  };

  const handleUpdatePassword = async (password: string, password_confirmation: string) => {
    if (selectedStaff?.id) {
      try {
        await updatePassword({ id: selectedStaff.id, password, password_confirmation });
        setResetPasswordFields(true);
        showSuccessToast('メンバーのパスワードを変更しました。');
      } catch (error) {
        showErrorToast('メンバーのパスワードを変更できませんでした。');
      }
    }
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !iam?.current_user?.is_admin) {
        return;
      }

      if (active.id !== over.id) {
        const oldIndex = displayMembers.findIndex((member) => member.id === Number(active.id));
        const newIndex = displayMembers.findIndex((member) => member.id === Number(over.id));

        if (oldIndex !== -1 && newIndex !== -1) {
          // 楽観的更新：即座にローカル状態を更新
          const reorderedMembers = arrayMove(displayMembers, oldIndex, newIndex);
          setDisplayMembers(reorderedMembers);

          try {
            // 実際の順序に合わせてorder_numberを再計算
            const finalUserOrders = reorderedMembers.map((member, index) => ({
              id: member.id,
              order_number: index + 1,
            }));

            await reorderMembers({ userOrders: finalUserOrders });
            // onSuccessでキャッシュが更新されるので、refetchは不要
            showSuccessToast('メンバーの順序を更新しました。');
          } catch (error) {
            // エラーが発生したら、元の状態に戻す
            setDisplayMembers(organization?.users || []);
            showErrorToast('メンバーの順序変更に失敗しました。');
          }
        }
      }
    },
    [displayMembers, iam?.current_user?.is_admin, reorderMembers, organization?.users]
  );

  const isAbleToAddStaff =
    iam?.current_user?.is_admin &&
    (organization?.contracted_seats ?? 0) > (activeMembers?.length ?? 0);

  return (
    <>
      {!isNewStaffPageOpen && !isStaffFormOpen && (
        <>
          <OrganizationSettingsTabs />
          <div className="mt-8 flex items-end justify-between">
            {isAbleToAddStaff && (
              <Button
                color="primary"
                size="md"
                className="h-[52px] w-[220px]"
                onClick={() => setIsNewStaffPageOpen(true)}
              >
                <div className="flex items-center gap-2">
                  <MaterialIcon name="add" />
                  新規アカウントを招待する
                </div>
              </Button>
            )}
            <p className="text-xs text-sub">
              {activeMembers?.length}/{organization?.contracted_seats} アカウント使用中
            </p>
          </div>

          <div className="mt-4 mb-20">
            <div className="overflow-visible border border-solid border-gray-200">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="w-full table-fixed">
                  <thead className="bg-gray_darken">
                    <tr>
                      <th
                        scope="col"
                        className="border-r border-b border-solid border-gray-200 px-6 py-3 text-left"
                      >
                        <div className="text-base font-medium text-gray-900">氏名</div>
                      </th>
                      <th
                        scope="col"
                        className="border-r border-b border-solid border-gray-200 px-6 py-3 text-left"
                      >
                        <div className="text-base font-medium text-gray-900">メールアドレス</div>
                      </th>
                      <th
                        scope="col"
                        className="w-[160px] border-r border-b border-solid border-gray-200 px-6 py-3 text-left"
                      >
                        <div className="text-base font-medium text-gray-900">権限</div>
                      </th>
                      {iam?.current_user?.is_admin && (
                        <th className="w-x3l border-b border-solid border-gray-200 py-3"></th>
                      )}
                    </tr>
                  </thead>
                  <SortableContext
                    items={displayMembers.map((member) => member.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody className="bg-white">
                      {displayMembers.map((member: User, index: number) => (
                        <SortableMemberRow
                          key={member.id}
                          member={member}
                          index={index}
                          totalLength={displayMembers.length}
                          isAdmin={iam?.current_user?.is_admin || false}
                          onEditStaff={handleEditStaff}
                          refetchOrganization={refetchOrganization}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
          </div>
        </>
      )}
      {isNewStaffPageOpen && <NewStaffPage onClose={() => handleNewStaffPageClose()} />}
      {isStaffFormOpen && selectedStaff && (
        <StaffForm
          currentUser={selectedStaff}
          updateInfo={handleUpdateInfo}
          updatePassword={handleUpdatePassword}
          resetPasswordFields={resetPasswordFields}
          onClose={() => setIsStaffFormOpen(false)}
        />
      )}
    </>
  );
}
